"use client";

import { Button } from "@/components/rac/button";
import { useComparisonStoreData } from "@/providers/global";
import { MapItemSUP } from "@/types/search";
import { SellerUnitsAndProjectsOne } from "@/types/sup";
import { trackEvent } from "@/utils/analytics/track-event";
import { s3Url } from "@/utils/s3-prefix";
import { ChevronUp, X, ChevronDown } from "lucide-react";
import Image from "next/image";
import { PropertySearchModal } from "./property-search-modal";
import { useRouter, usePathname } from "next/navigation";

// Union type to support both search results and project/unit page data
type ComparableProperty = MapItemSUP | SellerUnitsAndProjectsOne;

const MAX_COMPARE_PROPERTIES = 4;

export default function ComparisonTray() {
	// Use global comparison store
	const comparisonTray = useComparisonStoreData(state => state.comparisonTray);
	const toggleComparison = useComparisonStoreData(state => state.toggleComparison);
	const setComparisonTray = useComparisonStoreData(state => state.setComparisonTray);
	const isComparisonMinimized = useComparisonStoreData(state => state.isComparisonMinimized);
	const setIsComparisonMinimized = useComparisonStoreData(state => state.setIsComparisonMinimized);
	const router = useRouter();
	const pathname = usePathname();

	// Hide the tray if we're on the compare page
	if (pathname === "/compare") return null;

	if (!comparisonTray || comparisonTray.length === 0) return null;

	// If minimized, don't render the tray (badge will show instead)
	if (isComparisonMinimized) return null;

	const handleExpand = () => {
		// Build query params with property IDs and types
		const queryParams = comparisonTray
			.slice(0, MAX_COMPARE_PROPERTIES)
			.map((property, index) => {
				const id = property._id;
				const type = property.type;
				// For projects, also include projectId if available
				const actualId = type === "sp" || type === "spIdWithBedrooms" ? property.projectId || id : id;
				return `property${index + 1}=${actualId}&type${index + 1}=${type}`;
			})
			.join("&");

		trackEvent({
			google: {
				event: "expand_comparison_tray",
				options: {
					property_count: comparisonTray.length,
				},
			},
		});

		// Navigate to compare page with query params
		router.push(`/compare?${queryParams}`);
	};

	const handleRemoveProperty = (property: ComparableProperty) => {
		toggleComparison(property);
		trackEvent({
			google: {
				event: "remove_from_comparison",
				options: {
					property_id: property._id,
					property_type: property.type,
				},
			},
		});
	};

	const handleClearAll = () => {
		setComparisonTray([]);
		trackEvent({
			google: {
				event: "clear_comparison_tray",
				options: {
					property_count: comparisonTray.length,
				},
			},
		});
	};

	const handleMinimize = () => {
		setIsComparisonMinimized(true);
		trackEvent({
			google: {
				event: "minimize_comparison_tray",
				options: {
					property_count: comparisonTray.length,
				},
			},
		});
	};

	// Always show collapsed view - navigation handled by route
	return (
		<div className="fixed bottom-0 left-0 right-0 z-50">
			<div className="bg-white shadow-2xl border-t border-gray-200">
				{/* Property Cards - Horizontal Scroll with integrated header */}
				<div className="overflow-x-auto">
					<div className="flex items-center px-4 py-2 min-w-full">
						{/* Header section - inline with cards */}
						<div className="flex items-center gap-2 flex-shrink-0">
							<span className="font-semibold text-gray-900 text-sm">Compare Properties</span>
							<span className="text-xs text-gray-500">{comparisonTray.length}</span>
						</div>

						<div className="h-8 w-px bg-gray-200 flex-shrink-0 mx-3" />

						{/* Property cards container - always show 4 slots with equal distribution */}
						<div className="flex items-center justify-between gap-3 flex-1">
							{Array.from({ length: MAX_COMPARE_PROPERTIES }).map((_, index) => {
								const property = comparisonTray[index];

								if (property) {
									// Determine if it's a project or unit
									const isProject = property.type === "sp" || property.type === "spIdWithBedrooms";

									// Get appropriate name (fallback chain)
									const propertyName = property.projectName || property.localityName || "Property";

									// Get appropriate cover image with fallbacks
									const coverImage = property.cover || property.appCoverImage;

									// Get locality with fallback
									const locality = property.localityName || property.locality || "";

									// Render filled property card
									return (
										<div
											key={`slot-${index}-${property._id}`}
											className={`flex items-center gap-2 rounded-md px-2 py-1.5 relative group flex-shrink-0 flex-1 min-w-0 max-w-xs ${
												isProject ? "bg-gradient-to-r from-blue-50 to-indigo-50" : "bg-gray-50"
											}`}
										>
											<button
												onClick={() => handleRemoveProperty(property)}
												className="absolute -top-1 -right-1 z-10 bg-white rounded-full p-0.5 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity border border-gray-200"
												aria-label="Remove property"
											>
												<X className="w-3 h-3 text-gray-600" />
											</button>
											{coverImage && (
												<div className="relative w-16 h-12 rounded overflow-hidden flex-shrink-0">
													<Image
														src={coverImage.startsWith("http") ? coverImage : s3Url(coverImage)}
														alt={propertyName}
														fill
														className="object-cover"
													/>
												</div>
											)}
											<div className="flex flex-col justify-center min-w-0 pr-4">
												<div className="flex items-center gap-1">
													<h3 className="font-medium text-xs text-gray-900 truncate max-w-[150px]">{propertyName}</h3>
													{isProject && (
														<span className="text-[8px] px-1.5 py-0.5 bg-blue-500 text-white rounded-full font-medium flex-shrink-0">
															P
														</span>
													)}
												</div>
												<p className="text-[10px] text-gray-600 truncate max-w-[150px]">{locality}</p>
											</div>
										</div>
									);
								} else {
									// Render empty slot
									return (
										<PropertySearchModal
											key={`slot-${index}-empty`}
											trigger={
												<button className="flex-shrink-0 flex items-center gap-2 px-2 py-1.5 rounded-md border-2 border-dashed border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all group flex-1 min-w-0 max-w-xs w-full">
													<div className="w-16 h-12 rounded bg-gray-100 flex items-center justify-center">
														<svg
															className="w-6 h-6 text-gray-400 group-hover:text-gray-500"
															fill="none"
															stroke="currentColor"
															viewBox="0 0 24 24"
														>
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
														</svg>
													</div>
													<span className="text-xs text-gray-500 group-hover:text-gray-700 font-medium pr-4 whitespace-nowrap">
														Add Property
													</span>
												</button>
											}
											onSelectProperty={property => {
												toggleComparison(property);
												trackEvent({
													google: {
														event: "add_to_comparison_from_search",
														options: {
															property_id: property._id,
															property_type: property.type,
														},
													},
												});
											}}
											excludePropertyIds={comparisonTray.map(f => f._id)}
										/>
									);
								}
							})}
						</div>

						<div className="h-8 w-px bg-gray-200 flex-shrink-0 mx-3" />

						{/* Action buttons - inline */}
						<div className="flex items-center gap-2 flex-shrink-0">
							<Button variant="ghost" size="sm" onPress={handleClearAll} className="text-xs h-8">
								Clear All
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onPress={handleMinimize}
								className="flex items-center gap-1 text-xs h-8"
							>
								<span>Minimize</span>
								<ChevronDown className="w-3 h-3" />
							</Button>
							<Button variant="ghost" size="sm" onPress={handleExpand} className="flex items-center gap-1 text-xs h-8">
								<span>Expand</span>
								<ChevronUp className="w-3 h-3" />
							</Button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
